import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LucideAngularModule } from 'lucide-angular';
import { AccordionModule } from 'primeng/accordion';
import { SectionComponent } from 'src/app/shared/components/section/section.component';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { MethodologyService } from 'src/app/shared/services/methodology.service';

@Component({
  selector: 'app-help-how-it-works',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    LucideAngularModule,
    AccordionModule,
    SectionComponent,
  ],
  templateUrl: './help-how-it-works.component.html',
})
export class HelpHowItWorksComponent {
  private translocoService = inject(TranslocoService);
  protected packLimitationService = inject(PackLimitationsService);
  protected methodologyService = inject(MethodologyService);

  act4travelSteps = [
    {
      number: 1,
      titleKey: 'help.howItWorks.sections.act4travel.steps.calculate.title',
      descriptionKey:
        'help.howItWorks.sections.act4travel.steps.calculate.description',
      icon: 'calculator',
    },
    {
      number: 2,
      titleKey: 'help.howItWorks.sections.act4travel.steps.reduce.title',
      descriptionKey:
        'help.howItWorks.sections.act4travel.steps.reduce.description',
      icon: 'trending-down',
    },
    {
      number: 3,
      titleKey: 'help.howItWorks.sections.act4travel.steps.contribute.title',
      descriptionKey:
        'help.howItWorks.sections.act4travel.steps.contribute.description',
      icon: 'heart',
    },
    {
      number: 4,
      titleKey: 'help.howItWorks.sections.act4travel.steps.inspire.title',
      descriptionKey:
        'help.howItWorks.sections.act4travel.steps.inspire.description',
      icon: 'lightbulb',
    },
  ];

  dataInputMethods = [
    {
      titleKey: 'help.howItWorks.sections.dataInput.methods.manual.title',
      descriptionKey:
        'help.howItWorks.sections.dataInput.methods.manual.description',
      icon: 'edit',
    },
    {
      titleKey: 'help.howItWorks.sections.dataInput.methods.ai.title',
      descriptionKey:
        'help.howItWorks.sections.dataInput.methods.ai.description',
      icon: 'bot',
    },
    {
      titleKey: 'help.howItWorks.sections.dataInput.methods.bulk.title',
      descriptionKey:
        'help.howItWorks.sections.dataInput.methods.bulk.description',
      icon: 'layers',
    },
  ];

  calculationCategories = [
    {
      titleKey:
        'help.howItWorks.sections.toolOperation.categories.transport.title',
      descriptionKey:
        'help.howItWorks.sections.toolOperation.categories.transport.description',
      formulaKey:
        'help.howItWorks.sections.toolOperation.categories.transport.formula',
      icon: 'plane',
    },
    {
      titleKey:
        'help.howItWorks.sections.toolOperation.categories.accommodation.title',
      descriptionKey:
        'help.howItWorks.sections.toolOperation.categories.accommodation.description',
      icon: 'home',
    },
    {
      titleKey:
        'help.howItWorks.sections.toolOperation.categories.activities.title',
      descriptionKey:
        'help.howItWorks.sections.toolOperation.categories.activities.description',
      icon: 'compass',
    },
    {
      titleKey: 'help.howItWorks.sections.toolOperation.categories.food.title',
      descriptionKey:
        'help.howItWorks.sections.toolOperation.categories.food.description',
      icon: 'utensils',
    },
  ];

  climateProjectTypes = [
    {
      nameKey:
        'help.howItWorks.sections.carbonContribution.projectTypes.global.name',
      descriptionKey:
        'help.howItWorks.sections.carbonContribution.projectTypes.global.description',
      icon: 'globe',
    },
    {
      nameKey:
        'help.howItWorks.sections.carbonContribution.projectTypes.local.name',
      descriptionKey:
        'help.howItWorks.sections.carbonContribution.projectTypes.local.description',
      icon: 'map-pin',
    },
    {
      nameKey:
        'help.howItWorks.sections.carbonContribution.projectTypes.sustainable.name',
      descriptionKey:
        'help.howItWorks.sections.carbonContribution.projectTypes.sustainable.description',
      icon: 'leaf',
    },
  ];

  get ademeAirUrl(): string {
    const lang = this.translocoService.getActiveLang();
    return lang === 'fr'
      ? 'https://prod-basecarbonesolo.ademe-dri.fr/documentation/UPLOAD_DOC_FR/index.htm?aerien2.htm'
      : 'https://prod-basecarbonesolo.ademe-dri.fr/documentation/UPLOAD_DOC_EN/index.htm?aerien2.htm';
  }

  get nziUrl(): string {
    const lang = this.translocoService.getActiveLang();
    return lang === 'fr'
      ? 'https://www.net-zero-initiative.com/fr/10-principes'
      : 'https://www.net-zero-initiative.com/en/nzi-10-principles';
  }

  openDocument(docType: 'conditions' | 'privacy', event: Event): void {
    event.preventDefault();
    const lang = this.translocoService.getActiveLang();
    const docMap: Record<string, string> = {
      conditions: `assets/terms-and-conditions/${lang}/gt-conditions-${lang}.pdf`,
      privacy: `assets/terms-and-conditions/${lang}/gt-privacy-${lang === 'en' ? 'en' : 'statement-' + lang}.pdf`,
    };
    window.open(docMap[docType], '_blank');
  }

  netZeroPillars = [
    {
      letterKey: 'A',
      titleKey: 'help.howItWorks.sections.netZero.pillars.reduce.title',
      descriptionKey:
        'help.howItWorks.sections.netZero.pillars.reduce.description',
      color: 'bg-green-500',
    },
    {
      letterKey: 'B',
      titleKey: 'help.howItWorks.sections.netZero.pillars.avoid.title',
      descriptionKey:
        'help.howItWorks.sections.netZero.pillars.avoid.description',
      color: 'bg-blue-500',
    },
    {
      letterKey: 'C',
      titleKey: 'help.howItWorks.sections.netZero.pillars.sequester.title',
      descriptionKey:
        'help.howItWorks.sections.netZero.pillars.sequester.description',
      color: 'bg-purple-500',
    },
  ];
}
